import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import {
  getTopicsBySubjectKey,
} from "../../../helper/apicalls/course";
import {
  createSubjectFolder,
  getFolders,
  getQuestionBanks,
  updateFolder,
  deleteFolder,
  getInstitutionQuestionBanks,
  updateInstitutionQuestionBankFolder,
  deleteInstitutionQuestionBankFolder,
  getAdminQuestionBankFoldersInInstitution,
  getAdminQuestionBanksInInstitution,
} from "../../../helper/apicalls/questionBank";
import CreateFolderPopup from "../../common/CreateFolderPopup";
import {
  AddIcon,
  FolderIcon,
  ArrowBackIcon,
} from "../../../icons";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import GlobalLoader from "../../common/GlobalLoader";
import GenerateQuestionBankPopup from "../../common/GenerateQuestionBankPopup";
import QuestionBankViewPopup from "../../common/QuestionBankViewPopup";
import "./../../../styles/admin/questionBank.scss";

const QuestionBankTopics = () => {
  const { courseKey, subjectKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isInstitution =
    location.pathname.startsWith("/institution") ||
    location.pathname.startsWith("/teacher");
  const basePath = location.pathname.startsWith("/institution")
    ? "/institution/question_banks"
    : location.pathname.startsWith("/teacher")
    ? "/teacher/question_banks"
    : "/admin/question-bank";

  const role = Cookies.get("role");
  const permissions = Cookies.get("permissions");
  let permissionsData = [];
  try {
    const parsed = permissions ? JSON.parse(permissions) : [];
    permissionsData = Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error("Error parsing permissions:", e);
  }
  const questionBankPermission = permissionsData?.find(
    (permission) => permission?.permission_type === "questionbank"
  );
  const hasWritePermission = role === "admin" || (questionBankPermission ? !!questionBankPermission.write : false);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isTempSubject = subjectKey?.includes("-temp-");
  const [showGeneratePopup, setShowGeneratePopup] = useState(false);
  const [folders, setFolders] = useState([]);
  const [showCreateFolderPopup, setShowCreateFolderPopup] = useState(false);
  const [activeFolder, setActiveFolder] = useState(null);
  const [folderBanks, setFolderBanks] = useState([]);
  const [viewBankId, setViewBankId] = useState(null);
  const [isViewPopupOpen, setIsViewPopupOpen] = useState(false);
  const [folderMenuOpenId, setFolderMenuOpenId] = useState(null);
  const [editFolder, setEditFolder] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isTempSubject) {
        setTopics([]);

        return;
      }

      const topicsData = await getTopicsBySubjectKey(subjectKey);

      let foldersData = [];
      try {
        if (isInstitution) {
          const adminFolders = await getAdminQuestionBankFoldersInInstitution("subject", subjectKey);
          const institutionFolders = await getFolders("subject", subjectKey);
          foldersData = [
            ...adminFolders.map(f => ({ ...f, is_admin: true })),
            ...institutionFolders.map(f => ({ ...f, is_admin: false }))
          ];
        } else {
          foldersData = await getFolders("subject", subjectKey);
        }
      } catch (folderErr) {
        console.error(
          "Error fetching folders for Question Bank (subject level):",
          folderErr
        );
        foldersData = [];
      }

      if (Array.isArray(topicsData) && topicsData.length > 0) {
        setTopics(topicsData);
      } else {
        setTopics([]);
      }

      setFolders(Array.isArray(foldersData) ? foldersData : []);
    } catch (err) {
      console.error("Error fetching topics for Question Bank:", err);
      setError("Failed to fetch topics");
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFolderClick = async (folder) => {
    try {
      setLoading(true);
      setError(null);
      setActiveFolder(folder);
      let data;
      if (folder.is_admin) {
        data = await getAdminQuestionBanksInInstitution(folder.id);
        if (Array.isArray(data)) {
          data = data.map((qb) => ({ ...qb, is_admin: true }));
        }
      } else {
        const listQuestionBanks = isInstitution
          ? getInstitutionQuestionBanks
          : getQuestionBanks;
        data = await listQuestionBanks(folder.id);
        if (Array.isArray(data)) {
          data = data.map((qb) => ({ ...qb, is_admin: false }));
        }
      }
      setFolderBanks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching question banks for folder:", err);
      setError("Failed to fetch question banks");
      setFolderBanks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTopicClick = (topic) => {
    navigate(
      `${basePath}/course/${courseKey}/subject/${subjectKey}/topic/${topic.key}`
    );
  };

  const handleAddFolder = () => {
    setShowCreateFolderPopup(true);
  };

  const handleQuestionBankClick = (qb) => {
    if (!qb || !qb.id) return;
    setViewBankId(qb.id);
    setIsViewPopupOpen(true);
  };

  const handleBackFromFolder = () => {
    setActiveFolder(null);
    setFolderBanks([]);
  };

  const closeFolderMenu = () => {
    setFolderMenuOpenId(null);
  };

  const handleFolderMenuToggle = (id, e) => {
    e.stopPropagation();
    setFolderMenuOpenId((prev) => (prev === id ? null : id));
  };

  const handleFolderEdit = (folder, e) => {
    e.stopPropagation();
    setEditFolder(folder);
    closeFolderMenu();
  };

  const handleFolderDelete = (folder, e) => {
    e.stopPropagation();
    setFolderToDelete(folder);
    setIsConfirmDeleteOpen(true);
    closeFolderMenu();
  };

  useEffect(() => {
    fetchData();
  }, [courseKey, subjectKey, location]);

  return (
    <>
      <GlobalLoader visible={loading} text="Loading..." />
      <div className="question-bank-container">
        <div className="qb-card">
          <div className="qb-card-header">
            {activeFolder && (
              <button
                type="button"
                className="qb-back-icon-btn"
                onClick={handleBackFromFolder}
              >
                <ArrowBackIcon />
              </button>
            )}
            <span> Subject Question Bank</span>
            <div className="qb-card-actions">
              {!activeFolder && hasWritePermission && (
                <>
                  <button
                    type="button"
                    className="qb-add-btn"
                    onClick={handleAddFolder}
                  >
                    <AddIcon className="action-button-icon" />
                    <span>Add Folder</span>
                  </button>
                </>
              )}
              {activeFolder && !activeFolder.is_admin && hasWritePermission && (
                <>
                  <button
                    type="button"
                    className="qb-add-btn"
                    onClick={() => setShowGeneratePopup(true)}
                  >
                    <AddIcon className="action-button-icon" />
                    <span>Add Question Bank</span>
                  </button>
                </>
              )}
            </div>
          </div>

          {!activeFolder && (
            <div className="folder-grid">
              {!loading &&
                !error &&
                !isTempSubject &&
                topics?.map((top) => {
                  const id = top.id || top.key;
                  return (
                    <div
                      key={id}
                      className="folder-item"
                      onClick={() => handleTopicClick(top)}
                    >
                      <div className="folder-icon">
                        <FolderIcon style={{ color: "#fccc77" }} />
                      </div>
                      <div className="folder-name">{top.name}</div>
                    </div>
                  );
                })}

              {!loading &&
                !error &&
                !isTempSubject &&
                folders?.map((folder) => {
                  const id = folder.id || folder.key;
                  return (
                    <div
                      key={id}
                      className="folder-item"
                      onClick={() => handleFolderClick(folder)}
                    >
                      <div className="folder-icon">
                        <FolderIcon style={{ color: "var(--folder-bg)" }} />
                      </div>
                      <div className="folder-name">{folder.name}</div>
                      {isInstitution && folder.is_admin && (
                        <div className="qb-default-badge">Default</div>
                      )}
                      {!folder.is_admin && hasWritePermission && (
                        <button
                          type="button"
                          className="folder-menu-button"
                          onClick={(e) => handleFolderMenuToggle(id, e)}
                        >
                          <MoreVertIcon />
                        </button>
                      )}
                      {folderMenuOpenId === id && (
                        <div
                          className="folder-menu"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div
                            className="folder-menu-item"
                            onClick={(e) => handleFolderEdit(folder, e)}
                          >
                            Edit
                          </div>
                          <div
                            className="folder-menu-item"
                            onClick={(e) => handleFolderDelete(folder, e)}
                          >
                            Delete
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

              {!loading && !error && isTempSubject && (
                <div
                  className="folder-item file-item"
                  onClick={() => setShowGeneratePopup(true)}
                >
                  <div className="folder-icon">
                    <AddIcon />
                  </div>
                  <div className="folder-name">Add Question Bank</div>
                </div>
              )}
            </div>
          )}

          {activeFolder && (
            <div className="folder-grid">
              {!loading &&
                !error &&
                folderBanks?.map((qb) => (
                  <div
                    key={qb.id}
                    className="folder-item file-item"
                    onClick={() => handleQuestionBankClick(qb)}
                  >
                    <div className="folder-icon">📄</div>
                    <div className="folder-name">{qb.name || qb.id}</div>
                    {isInstitution && qb.is_admin && (
                      <div className="qb-default-badge">Default</div>
                    )}
                  </div>
                ))}

              {error && <div className="qb-error">{error}</div>}
            </div>
          )}
        </div>
      </div>

      <GenerateQuestionBankPopup
        open={showGeneratePopup}
        handleClose={() => setShowGeneratePopup(false)}
        context="subject"
        contextKey={subjectKey}
        folderId={activeFolder ? activeFolder.id : null}
        onSave={(payload) => {
          setShowGeneratePopup(false);
          if (activeFolder) {
            handleFolderClick(activeFolder);
          } else {
            fetchData();
          }
        }}
      />

      <QuestionBankViewPopup
        open={isViewPopupOpen}
        questionBankId={viewBankId}
        context="subject"
        contextKey={subjectKey}
        isAdminBank={activeFolder?.is_admin || false}
        onClose={() => {
          setIsViewPopupOpen(false);
          setViewBankId(null);
        }}
        onAddNext={(questionBankId) => {
          setShowGeneratePopup(true);
        }}
      />

      <CreateFolderPopup
        open={showCreateFolderPopup}
        handleClose={() => setShowCreateFolderPopup(false)}
        title="subject"
        contextKey={subjectKey}
        mode="create"
        onSave={async (payload) => {
          try {
            setLoading(true);
            await createSubjectFolder(payload.title, payload.key, payload.name);
            await fetchData();
          } catch (err) {
            console.error("Error creating subject folder:", err);
            setError("Failed to create folder");
          } finally {
            setLoading(false);
          }
        }}
      />

      <CreateFolderPopup
        open={!!editFolder}
        handleClose={() => setEditFolder(null)}
        title="subject"
        contextKey={subjectKey}
        mode="edit"
        initialName={editFolder?.name || ""}
        folderId={editFolder?.id}
        onSave={async (payload) => {
          try {
            if (!payload.id) return;
            setLoading(true);
            if (isInstitution) {
              await updateInstitutionQuestionBankFolder({
                id: payload.id,
                name: payload.name,
              });
            } else {
              await updateFolder(payload.id, payload.name);
            }
            await fetchData();
          } catch (err) {
            console.error("Error updating subject folder:", err);
            setError("Failed to update folder");
          } finally {
            setLoading(false);
            setEditFolder(null);
          }
        }}
      />

      <DeleteConfirmPopUp
        isConfirmDeleteOpen={isConfirmDeleteOpen}
        setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
        title="Delete folder"
        message={`Are you sure you want to delete the folder "${
          folderToDelete?.name || ""
        }"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        handleDelete={async () => {
          if (!folderToDelete?.id) {
            setIsConfirmDeleteOpen(false);
            return;
          }
          try {
            setLoading(true);
            if (isInstitution) {
              await deleteInstitutionQuestionBankFolder(folderToDelete.id);
            } else {
              await deleteFolder(folderToDelete.id);
            }
            await fetchData();
          } catch (err) {
            console.error("Error deleting subject folder:", err);
            setError("Failed to delete folder");
          } finally {
            setLoading(false);
            setIsConfirmDeleteOpen(false);
            setFolderToDelete(null);
          }
        }}
      />
    </>
  );
};

export default QuestionBankTopics;
